/**
 * Materialliste (Kapitel J).
 *
 * Zwei Ausgabetiefen aus derselben Datenbasis (PV-J03): pauschal fuer das
 * Kundenangebot, detailliert fuer die interne Kommissionierung. Umgeschaltet
 * wird nur die Ansicht — gerechnet wird einmal.
 *
 * Positionen ohne hinterlegten Preis stehen ohne Betrag da und werden oben
 * gezaehlt. Das ist Absicht: „auf Anfrage" als Nullpreis in ein Angebot zu
 * schreiben, waere der teuerste Fehler, den dieses Programm machen koennte.
 */

import { useState } from 'react';

import { warengruppenname, type Position, type Warengruppe } from '@pv/geometrie';

import { neueId, speichere } from '../daten/db.ts';
import type { Anlagendaten } from '../anlage.ts';
import type { Materialposition } from '../typen.ts';

interface Eigenschaften {
  projektId: string;
  anlage: Anlagendaten;
  materialpositionen: Materialposition[];
  /**
   * Fuer die Montage: Mengen ja, Preise nein. Die Preise kommen ohnehin nicht
   * auf ihr Geraet (siehe apps/api/src/sync.ts) — hier faellt nur weg, was
   * dann leer oder irrefuehrend dastuende.
   */
  ohnePreise?: boolean;
}

type Tiefe = 'pauschal' | 'detailliert';

const WARENGRUPPEN: Warengruppe[] = [
  'module',
  'wechselrichter',
  'speicher',
  'montagematerial',
  'elektromaterial',
  'arbeit',
  'sonstiges',
];

export function Materialpanel(e: Eigenschaften): React.JSX.Element {
  const { projektId, anlage, materialpositionen } = e;
  const [tiefe, setTiefe] = useState<Tiefe>('pauschal');
  const [neu, setNeu] = useState({ bezeichnung: '', menge: '1', einheit: 'Stk', preis: '' });

  const liste = tiefe === 'pauschal' ? anlage.material.pauschal : anlage.material.detailliert;

  async function ergaenze(): Promise<void> {
    if (!neu.bezeichnung.trim()) return;
    const position: Materialposition = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: projektId,
      artikel_id: null,
      bezeichnung: neu.bezeichnung.trim(),
      einheit: neu.einheit,
      menge: Number(String(neu.menge).replace(',', '.')) || 1,
      // Ein Preis wird nie geraten: Was keine Zahl ist, bleibt ohne Betrag.
      einzelpreis: Number.isFinite(Number(String(neu.preis).replace(',', '.'))) && String(neu.preis).trim() !== ''
        ? Number(String(neu.preis).replace(',', '.'))
        : null,
      warengruppe: 'sonstiges',
    };
    await speichere('materialpositionen', position, {
      projekt_id: position.projekt_id,
      bezeichnung: position.bezeichnung,
      einheit: position.einheit,
      menge: position.menge,
      einzelpreis: position.einzelpreis,
      warengruppe: position.warengruppe,
    });
    setNeu({ bezeichnung: '', menge: '1', einheit: 'Stk', preis: '' });
  }

  return (
    <div>
      <h2>Materialliste</h2>

      <div className="knopfreihe">
        <button aria-pressed={tiefe === 'pauschal'} onClick={() => setTiefe('pauschal')}>
          Fürs Angebot
        </button>
        <button aria-pressed={tiefe === 'detailliert'} onClick={() => setTiefe('detailliert')}>
          Für die Kommissionierung
        </button>
      </div>

      {anlage.material.fehlendeMaterialsaetze.length > 0 && (
        <div className="hinweis">
          Für {anlage.material.fehlendeMaterialsaetze.join(', ')} ist kein Materialsatz hinterlegt.
          Die Module stehen in der Liste, die Anbauteile fehlen.
        </div>
      )}

      {!e.ohnePreise && anlage.material.ohnePreis.length > 0 && (
        <div className="hinweis">
          {anlage.material.ohnePreis.length} Position(en) ohne hinterlegten Preis. Die betroffenen
          Warengruppen werden ohne Summe ausgewiesen, statt zu niedrig.
        </div>
      )}

      <div className="tabellenrahmen" style={{ overflowX: 'auto', margin: '1rem 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={kopf}>Position</th>
              <th style={{ ...kopf, textAlign: 'right' }}>Menge</th>
              {!e.ohnePreise && <th style={{ ...kopf, textAlign: 'right' }}>Einzel</th>}
              {!e.ohnePreise && <th style={{ ...kopf, textAlign: 'right' }}>Summe</th>}
            </tr>
          </thead>
          <tbody>
            {liste.map((position, index) => (
              <tr key={index}>
                <td style={zelle}>
                  {position.bezeichnung}
                  {position.herkunft === 'manuell' && (
                    <span style={{ color: 'var(--grau)', fontSize: '0.7rem' }}> · von Hand</span>
                  )}
                  {tiefe === 'detailliert' && (
                    <span style={{ display: 'block', color: 'var(--grau)', fontSize: '0.7rem' }}>
                      {warengruppenname(position.warengruppe)}
                    </span>
                  )}
                </td>
                <td style={{ ...zelle, textAlign: 'right' }}>
                  {position.menge} {position.einheit}
                </td>
                {!e.ohnePreise && (
                  <td style={{ ...zelle, textAlign: 'right' }}>{euro(position.einzelpreis)}</td>
                )}
                {!e.ohnePreise && (
                  <td style={{ ...zelle, textAlign: 'right' }}>{euro(summe(position))}</td>
                )}
              </tr>
            ))}
            {liste.length === 0 && (
              <tr>
                <td style={zelle} colSpan={4}>
                  Noch nichts zu rechnen — erst belegen, dann Komponenten wählen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!e.ohnePreise && (
        <>
      <dl className="zahlenblock">
        <div>
          <dt>Einkauf</dt>
          <dd>{euro(anlage.material.summeEinkauf)}</dd>
        </div>
        <div>
          <dt>Mit Aufschlag</dt>
          <dd>{euro(anlage.material.summeVerkauf)}</dd>
        </div>
      </dl>

        </>
      )}

      {!e.ohnePreise && (
        <>
      <h2 style={{ marginTop: '1.5rem' }}>Position ergänzen</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--grau)', margin: '0 0 0.5rem' }}>
        Es gibt immer den Sonderfall — Gerüst, Kranstunde, Zusatzarbeit.
      </p>
      <div className="feld">
        <input
          placeholder="Bezeichnung"
          value={neu.bezeichnung}
          onChange={(ereignis) => setNeu({ ...neu, bezeichnung: ereignis.target.value })}
        />
      </div>
      <div className="knopfreihe">
        <input
          type="number"
          step={0.1}
          value={neu.menge}
          onChange={(ereignis) => setNeu({ ...neu, menge: ereignis.target.value })}
          style={{ width: '5rem' }}
          aria-label="Menge"
        />
        <input
          value={neu.einheit}
          onChange={(ereignis) => setNeu({ ...neu, einheit: ereignis.target.value })}
          style={{ width: '5rem' }}
          aria-label="Einheit"
        />
        <input
          type="number"
          step={0.01}
          placeholder="Einzelpreis"
          value={neu.preis}
          onChange={(ereignis) => setNeu({ ...neu, preis: ereignis.target.value })}
          style={{ width: '7rem' }}
          aria-label="Einzelpreis"
        />
        <button className="wichtig" onClick={() => void ergaenze()}>
          Hinzufügen
        </button>
      </div>

      {materialpositionen
        .filter((p) => !p.geloescht_am)
        .map((position) => (
          <div
            key={position.id}
            style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
              borderTop: '1px solid var(--linie)',
              padding: '0.4rem 0',
              fontSize: '0.85rem',
            }}
          >
            <span style={{ flex: 1 }}>{position.bezeichnung}</span>
            <span>
              {position.menge} {position.einheit}
            </span>
            <button
              onClick={() =>
                void speichere('materialpositionen', position, {
                  geloescht_am: new Date().toISOString(),
                })
              }
            >
              ×
            </button>
          </div>
        ))}

        </>
      )}

      <p style={{ fontSize: '0.75rem', color: 'var(--grau)', marginTop: '1.5rem' }}>
        Warengruppen: {WARENGRUPPEN.map(warengruppenname).join(', ')}. Aufschläge je Gruppe stehen
        in den Einstellungen.
      </p>
    </div>
  );
}

// Kopf und Zelle kommen aus dem CSS (th, td); hier bleibt nur die Ausrichtung.
const kopf: React.CSSProperties = {};

const zelle: React.CSSProperties = {};

function summe(position: Position): number | undefined {
  // null wie undefined behandeln: beides heisst "kein Preis hinterlegt".
  if (position.einzelpreis == null) return undefined;
  return position.einzelpreis * position.menge;
}

function euro(betrag: number | null | undefined): string {
  if (betrag == null) return '—';
  return betrag.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
}
